<div class="flex flex-col gap-4 flex-1 min-h-0">
  <div class="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto pr-1">
    @if (hasBlockedAddendums()) {
      <p class="m-0 text-body-sm text-surface-700">
        The following addendum(s) are already part of an envelope. If you wish to proceed, you must "Void" the envelope
        first — or close this dialog and deselect them to proceed.
      </p>

      <div class="flex flex-col gap-2">
        <span class="inline-flex items-center gap-2 text-body-sm-600 text-red-600">
          <i class="pi pi-lock text-body-sm"></i>
          Already in Envelope ({{ blockedAddendums().length }})
        </span>
        <dsm-sign-off-addendum-list
          [addendums]="blockedAddendums()"
          [highlightedIds]="blockedIds()"
          [rowActionTemplate]="blockedRowAction"
          [showTotal]="false"
        />
      </div>

      @if (sendableAddendums().length) {
        <div class="flex flex-col gap-2">
          <span class="inline-flex items-center gap-2 text-body-sm-600 text-emerald-700">
            <i class="pi pi-check-circle text-body-sm"></i>
            Ready to Send ({{ sendableAddendums().length }})
          </span>
          <dsm-sign-off-addendum-list [addendums]="sendableAddendums()" [showTotal]="false" />
        </div>
      }
    } @else {
      <p class="m-0 text-body-sm text-surface-700">
        These Addendums will be attached to the Envelope and sent for signature.
      </p>

      <dsm-sign-off-addendum-list [addendums]="addendums()" [showTotal]="false" emptyMessage="No addendums selected." />
    }

    <ng-template #blockedRowAction let-addendum>
      @if (isBlocked(addendum)) {
        <div class="absolute top-1/2 -translate-y-1/2 right-12 flex items-center gap-1">
          <!-- <span class="text-body-2xs text-red-600 uppercase tracking-wide">{{ blockedReason(addendum) }}</span> -->
          @if (canViewEnvelope(addendum)) {
            <p-button
              icon="pi pi-eye"
              label="View Envelope"
              size="small"
              [outlined]="true"
              [rounded]="true"
              (onClick)="onViewEnvelope(addendum)"
            />
          }
        </div>
      }
    </ng-template>

    <!-- Include builder -->
    <div class="flex items-center gap-3 border border-slate-200 rounded-md p-4">
      <p-button
        label="TH"
        severity="secondary"
        [outlined]="true"
        [rounded]="false"
        [raised]="false"
        [text]="false"
        [link]="false"
        size="small"
        styleClass="!rounded-full !w-9 !h-9 !p-0 shrink-0 !border-amber-200 !bg-amber-50 !text-amber-800"
        [attr.aria-hidden]="true"
        [tabindex]="-1"
      />
      <div class="flex flex-col min-w-0">
        <span class="text-body-600 text-surface-700 leading-none">Include Builder</span>
        <span class="text-body-xs text-surface-500 mt-1">Signs the same envelope</span>
      </div>
      <p-toggleswitch
        class="ml-auto"
        [ngModel]="includeBuilder()"
        (ngModelChange)="includeBuilder.set($event)"
        inputId="includeBuilder"
        ariaLabel="Include builder in the envelope"
      />
    </div>

    <!-- Document source -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div
        class="flex items-center gap-3 border !rounded-xl p-4 cursor-pointer transition-colors"
        [class.border-primary-500]="!isCustomSource()"
        [class.border-slate-200]="isCustomSource()"
        (click)="selectDocumentSource('Default')"
      >
        <p-radioButton
          name="documentSource"
          inputId="documentSourceDefault"
          value="Default"
          [ngModel]="documentSource()"
          (ngModelChange)="selectDocumentSource($event)"
        />
        <div class="flex flex-col min-w-0">
          <span class="text-body-600 text-surface-700 leading-none">Default Document</span>
          <span class="text-body-xs text-surface-500 mt-1">Generated by the system</span>
        </div>
        <p-button
          class="ml-auto shrink-0"
          icon="pi pi-download"
          label="Download PDF"
          size="small"
          [outlined]="true"
          [disabled]="sendableAddendums().length === 0"
          [loading]="isPreviewingDefaultDocument()"
          (onClick)="$event.stopPropagation(); viewDefaultDocument()"
        />
      </div>

      <div class="flex flex-col gap-1">
        <div
          class="flex items-center gap-3 border !rounded-xl p-4 cursor-pointer transition-colors"
          [class.border-red-400]="!!customDocumentError()"
          [class.border-primary-500]="isCustomSource() && !customDocumentError()"
          [class.border-slate-200]="!isCustomSource() && !customDocumentError()"
          (click)="selectDocumentSource('Custom')"
        >
          <p-radioButton
            name="documentSource"
            inputId="documentSourceCustom"
            value="Custom"
            [ngModel]="documentSource()"
            (ngModelChange)="selectDocumentSource($event)"
          />
          <div class="flex flex-col min-w-0">
            <span class="text-body-600 text-surface-700 leading-none">Additional Document</span>
            @if (customDocument()) {
              <span class="text-body-xs text-surface-700 mt-1 truncate" [pTooltip]="customDocument()!.name">
                {{ customDocument()!.name }}
              </span>
            } @else {
              <span class="text-body-xs text-surface-500 mt-1">Appended to the default document</span>
            }
          </div>
          <p-button
            class="ml-auto shrink-0"
            icon="pi pi-upload"
            [label]="customDocument() ? 'Replace' : 'Upload'"
            size="small"
            [outlined]="true"
            [loading]="isUploading()"
            (onClick)="$event.stopPropagation(); customDocumentInput.click()"
          />
          <input
            #customDocumentInput
            type="file"
            class="hidden"
            accept="application/pdf"
            (click)="$event.stopPropagation()"
            (change)="onCustomDocumentSelected($event)"
          />
        </div>

        @if (customDocumentError()) {
          <span class="inline-flex items-start gap-1 text-body-xs text-red-600">
            <i class="pi pi-exclamation-circle text-body-2xs mt-0.5"></i>
            {{ customDocumentError() }}
          </span>
        }
        @for (warning of warnings(); track warning) {
          <span class="inline-flex items-start gap-1 text-body-xs text-amber-700">
            <i class="pi pi-exclamation-triangle text-body-2xs mt-0.5"></i>
            {{ warning }}
          </span>
        }
        @if (needsAcknowledgement()) {
          <label class="flex items-start gap-2 text-body-xs text-surface-600 cursor-pointer mt-1">
            <input
              type="checkbox"
              class="mt-0.5"
              [checked]="acknowledgeMissingSigners()"
              (change)="acknowledgeMissingSigners.set($any($event.target).checked)"
            />
            <span>Send anyway — I accept the missing signer(s): {{ missingSigners().join(', ') }}</span>
          </label>
        }
      </div>
    </div>
  </div>

  <div class="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-200 shrink-0">
    <span class="text-body-sm text-surface-500">
      Total:
      <span class="text-body-600 text-emerald-700 ml-1">{{ total() | currencyFormat }}</span>
    </span>
    <div class="flex items-center gap-2 ml-auto">
      <p-button label="Cancel" severity="secondary" type="button" (onClick)="close()" />
      <p-button
        label="Sign Now"
        [outlined]="true"
        type="button"
        [disabled]="isSubmitDisabled()"
        [loading]="isSigningNow()"
        (onClick)="signNow()"
      />
      <p-button
        label="Send via Email"
        type="button"
        [disabled]="isSubmitDisabled()"
        [loading]="isSendingViaEmail()"
        (onClick)="sendViaEmail()"
      />
    </div>
  </div>
</div>
